import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Camera,
  CheckCircle2,
  LogOut,
  AlertTriangle,
  Loader2,
  Fingerprint,
  Info,
  Volume2,
  VolumeX,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";

// Fase F — Totem de Batida de Ponto (perfil timeclock_kiosk)
export default function Kiosk() {
  const { user, company, logout } = useAuth();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const [now, setNow] = useState(new Date());
  const [phase, setPhase] = useState("ready"); // ready | submitting | result
  const [result, setResult] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [camError, setCamError] = useState(false);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("kiosk_voice") !== "off");

  const toggleVoice = () => {
    const nv = !voiceOn;
    setVoiceOn(nv);
    localStorage.setItem("kiosk_voice", nv ? "on" : "off");
    if (!nv) window.speechSynthesis?.cancel();
  };

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 960 } },
          audio: false,
        });
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setCamError(false);
      } catch {
        setCamError(true);
      }
    })();
    return () => streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  // 10s countdown, then reload the page for a fresh punch (camera restarts)
  useEffect(() => {
    if (phase !== "result") return;
    setCountdown(10);
    const t = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(t);
          window.location.reload();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [phase]);

  const speak = useCallback(
    (text) => {
      try {
        if (!voiceOn || !window.speechSynthesis || !text) return;
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(text);
        u.lang = "pt-BR";
        u.rate = 1;
        const voice = window.speechSynthesis
          .getVoices()
          .find((v) => v.lang?.toLowerCase().startsWith("pt"));
        if (voice) u.voice = voice;
        window.speechSynthesis.speak(u);
      } catch (err) {
        console.debug("speech synthesis failed (voz é opcional)", err);
      }
    },
    [voiceOn]
  );

  // Fala o resultado da batida em voz alta (apenas o primeiro nome cadastrado)
  useEffect(() => {
    if (phase !== "result" || !result) return;
    const firstName = (result.first_name || "").trim();
    if (result.recognized && !result.all_done && result.label) {
      const hello = result.welcome ? "Bem-vindo" : "Até logo";
      speak(`${hello}, ${firstName}! ${result.label} registrada com sucesso.`);
    } else if (result.all_done) {
      speak(`${firstName}, suas quatro batidas de hoje já foram registradas.`);
    } else {
      speak("Não foi possível reconhecer você. Tente novamente.");
    }
    return () => window.speechSynthesis?.cancel();
  }, [phase, result, speak]);

  const submitBlob = async (blob) => {
    setPhase("submitting");
    try {
      const fd = new FormData();
      fd.append("file", blob, "kiosk.jpg");
      fd.append("purpose", "ponto_totem");
      const { data: up } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data } = await api.post("/timeclock/kiosk/punch", { selfie_url: up.url });
      setResult(data);
    } catch (err) {
      setResult({
        recognized: false,
        message: err?.response?.data?.detail || "Falha ao registrar. Tente novamente.",
      });
    }
    setPhase("result");
  };

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      fileRef.current?.click();
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((b) => b && submitBlob(b), "image/jpeg", 0.85);
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) submitBlob(f);
    if (fileRef.current) fileRef.current.value = "";
  };

  const punched = result?.recognized && !result?.all_done && result?.label;

  return (
    <div
      className="min-h-screen bg-stone-950 text-white flex flex-col overflow-hidden"
      data-testid="kiosk-page"
    >
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-stone-900">
        <div className="flex items-center gap-2.5">
          <img src="/logo.png" alt="Ultra" className="h-8 w-8 rounded-lg" />
          <div>
            <div className="font-display font-extrabold tracking-tight leading-none">
              Ultra · Ponto
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-400/80 font-body">
              {company?.trade_name || company?.legal_name || user?.name || "Totem"}
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-stone-500 hover:text-white hover:bg-stone-900"
          onClick={async () => {
            await logout();
            navigate("/login");
          }}
          data-testid="kiosk-logout"
        >
          <LogOut className="h-4 w-4 mr-1.5" /> Sair do totem
        </Button>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center gap-6 px-4 py-6">
        {/* Live clock */}
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-stone-500 font-body">
            {now.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}
          </div>
          <div
            className="font-display text-7xl sm:text-8xl font-black tabular-nums leading-none mt-1"
            data-testid="kiosk-clock"
          >
            {now.toLocaleTimeString("pt-BR")}
          </div>
        </div>

        {phase === "result" && result ? (
          <div className="text-center max-w-xl space-y-4" data-testid="kiosk-result">
            {punched ? (
              <>
                <div className="mx-auto h-24 w-24 rounded-full bg-emerald-500/15 border-2 border-emerald-500 flex items-center justify-center">
                  <CheckCircle2 className="h-12 w-12 text-emerald-400" />
                </div>
                <div className="font-display text-4xl font-extrabold" data-testid="kiosk-greeting">
                  {result.greeting}
                </div>
                <div className="text-lg text-stone-300 font-body" data-testid="kiosk-result-message">
                  {result.label} registrada às{" "}
                  <span className="font-display font-bold text-white tabular-nums">
                    {result.time}
                  </span>
                </div>
                {result.status === "pending_review" && (
                  <div className="inline-flex items-center gap-1.5 text-xs text-amber-400 font-body bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1">
                    <Info className="h-3.5 w-3.5" /> Registro em revisão pelo RH
                  </div>
                )}
              </>
            ) : result.all_done ? (
              <>
                <div className="mx-auto h-24 w-24 rounded-full bg-sky-500/15 border-2 border-sky-500 flex items-center justify-center">
                  <Info className="h-12 w-12 text-sky-400" />
                </div>
                <div className="font-display text-2xl font-bold" data-testid="kiosk-result-message">
                  {result.message}
                </div>
              </>
            ) : (
              <>
                <div className="mx-auto h-24 w-24 rounded-full bg-amber-500/15 border-2 border-amber-500 flex items-center justify-center">
                  <AlertTriangle className="h-12 w-12 text-amber-400" />
                </div>
                <div className="font-display text-2xl font-bold">Não reconhecido</div>
                <div className="text-stone-400 font-body" data-testid="kiosk-result-message">
                  {result.message}
                </div>
              </>
            )}
            <div className="text-sm text-stone-500 font-body" data-testid="kiosk-countdown">
              Nova batida em <span className="font-display font-bold text-stone-300">{countdown}s</span>
            </div>
          </div>
        ) : (
          <>
            {/* Camera frame */}
            <div className="relative">
              <div className="h-64 w-64 sm:h-72 sm:w-72 rounded-full overflow-hidden ring-4 ring-emerald-600/60 bg-stone-900 flex items-center justify-center">
                {camError ? (
                  <div className="text-center px-6 text-stone-500 font-body text-sm">
                    <Camera className="h-8 w-8 mx-auto mb-2" />
                    Câmera indisponível — toque em Registrar para enviar uma foto
                  </div>
                ) : (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="h-full w-full object-cover scale-x-[-1]"
                    data-testid="kiosk-video"
                  />
                )}
              </div>
              {phase === "submitting" && (
                <div className="absolute inset-0 rounded-full bg-stone-950/70 flex flex-col items-center justify-center gap-2">
                  <Loader2 className="h-10 w-10 animate-spin text-emerald-400" />
                  <span className="text-sm font-body text-stone-300">Identificando você…</span>
                </div>
              )}
            </div>

            <div className="text-center text-stone-400 font-body text-sm max-w-md">
              Posicione o rosto no círculo e toque no botão. Você será identificado
              automaticamente pela selfie.
            </div>

            <Button
              onClick={capture}
              disabled={phase === "submitting"}
              className="h-16 px-10 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-lg font-display font-bold"
              data-testid="kiosk-punch-btn"
            >
              <Fingerprint className="h-6 w-6 mr-3" /> Registrar ponto
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="user"
              className="hidden"
              onChange={onPickFile}
              data-testid="kiosk-file-input"
            />
          </>
        )}
      </main>

      <footer className="flex items-center justify-center gap-4 text-[10px] text-stone-600 font-body py-3 border-t border-stone-900">
        <span>
          Totem de ponto Ultra · reconhecimento facial por IA · batidas: entrada, intervalo,
          volta e saída
        </span>
        <button
          type="button"
          onClick={toggleVoice}
          className="inline-flex items-center gap-1 text-stone-600 hover:text-stone-300 transition-colors"
          data-testid="kiosk-voice-toggle"
        >
          {voiceOn ? <Volume2 className="h-3 w-3" /> : <VolumeX className="h-3 w-3" />}
          {voiceOn ? "Desligar voz" : "Ativar voz"}
        </button>
      </footer>
    </div>
  );
}
